/**
 * Named sane-default label presets for dashboard Quick/Card device widgets.
 * Keys are status names (or XT1 roles) produced by `status_configurator`.
 * Missing live statuses are skipped at resolve time.
 */
export type DashDeviceLabelPresetId = keyof typeof DASH_DEVICE_LABEL_PRESETS;

export type DashDeviceLabelPreset = {
	/** Ordered status names / roles to show when present. */
	labels: string[];
	/**
	 * When true, the first resolved label is promoted to the right slot (main),
	 * and only the remaining labels fill the info row (secondary…).
	 * Used for meter-style devices with no on/off control (e.g. Pro 3EM).
	 */
	promoteFirstToRightSlot?: boolean;
};

/** Optional override shape reserved for later customisation. */
export type DashDeviceLabelConfig =
	| DashDeviceLabelPresetId
	| {
			preset: DashDeviceLabelPresetId;
			extra?: string[];
			hide?: string[];
	  };

export const DASH_DEVICE_LABEL_PRESETS = {
	'light-dimmer': {
		labels: ['brightness'],
	},
	/**
	 * Relay with power metering — current power is the main (right-slot) status.
	 * Voltage fills the info row when present. Gen2 switch energy is `total`+advLabel,
	 * so it is not included here.
	 */
	'relay-pm': {
		labels: ['power', 'voltage'],
		promoteFirstToRightSlot: true,
	},
	/** Pro 3EM / EM63 / etc. — power is main (right slot), voltage is secondary (info). */
	emeter: {
		labels: ['power', 'voltage'],
		promoteFirstToRightSlot: true,
	},
	/** H&T / BLU HT — temperature main, humidity secondary. */
	'sensor-ht': {
		labels: ['temperature', 'humidity'],
		promoteFirstToRightSlot: true,
	},
	/**
	 * Shelly Presence — illuminance only in the info row.
	 * Object detection lives in the right slot (RadarControl); zone counts are omitted.
	 */
	'sensor-presence': {
		labels: ['illuminance'],
	},
	/**
	 * Door/Window — open/closed main (right slot), lux secondary (info).
	 * Tilt/angle is included when present (skipped at resolve time otherwise).
	 */
	'sensor-dw': {
		labels: ['door', 'tilt', 'lux'],
		promoteFirstToRightSlot: true,
	},
	'xt1-irrigation': {
		labels: ['average_temperature', 'last_precipitation'],
	},
	'xt1-thermostat': {
		labels: ['curr_temperature', 'current_temperature', 'humidity', 'current_humidity'],
	},
	'xt1-valve': {
		labels: ['position', 'has_power'],
	},
	/**
	 * Wall Display — ambient sensors in the info row (Mode B: switch / thermostat
	 * control stays in the right slot). Thermostat output (`power`) and media
	 * `playback` are omitted: PeekTray leftover handling still surfaces them.
	 */
	'wall-display': {
		labels: ['illuminance', 'temperature', 'humidity', 'thermostat_temperature'],
	},
} as const satisfies Record<string, DashDeviceLabelPreset>;

/**
 * Category / family fallback when a model or mode has no `dashInfoLabels` entry.
 * Keep this sparse — prototypes seed explicit presets first.
 */
export const DASH_DEVICE_LABEL_CATEGORY_FALLBACK: Record<string, DashDeviceLabelPresetId> = {
	light: 'light-dimmer',
	relay: 'relay-pm',
	emeter: 'emeter',
	irrigation: 'xt1-irrigation',
	thermostat: 'xt1-thermostat',
	valve: 'xt1-valve',
};

export const resolvePresetId = (
	config: DashDeviceLabelConfig | undefined,
): DashDeviceLabelPresetId | undefined => {
	if (!config) return undefined;
	if (typeof config === 'string') return config;
	return config.preset;
};

export const getPreset = (
	config: DashDeviceLabelConfig | undefined,
): DashDeviceLabelPreset | undefined => {
	const presetId = resolvePresetId(config);
	if (!presetId) return undefined;
	return DASH_DEVICE_LABEL_PRESETS[presetId];
};

export const expandLabelConfig = (config: DashDeviceLabelConfig | undefined): string[] => {
	const preset = getPreset(config);
	if (!preset) return [];

	const labels = [...preset.labels];

	if (typeof config === 'object') {
		if (config.extra?.length) {
			for (const name of config.extra) {
				if (!labels.includes(name)) labels.push(name);
			}
		}
		if (config.hide?.length) {
			return labels.filter((name) => !config.hide?.includes(name));
		}
	}

	return labels;
};
